import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save, Send, Upload, FileText, Code, AlertTriangle, Eye } from 'lucide-react';
import { Card, Badge, Button } from '../../components/ui';
import { api } from '../../lib/api';
import { TemplateSelector, SolidityCodeViewer, AuditStatusBadge, DeployPreviewPanel } from '../../components/smart-contracts';
import { useTemplates } from '../../hooks/useSmartContractTemplates';
import { useGeneratePreview } from '../../hooks/useDeployPreview';

interface AssetDocument {
  id: string;
  name: string;
  type: string;
  status: string;
}

interface Asset {
  id: string;
  name: string;
  symbol: string;
  category: string;
  description?: string;
  totalValue: number;
  unitPrice: number;
  maturityDate?: string;
  yieldRate?: number;
  yieldFrequency?: string;
  status: string;
  documents?: AssetDocument[];
}

interface AssetFormData {
  name: string;
  symbol: string;
  category: string;
  description: string;
  totalValue: number;
  unitPrice: number;
  maturityDate?: string;
  yieldRate?: number;
  yieldFrequency?: string;
}

const categoryLabels: Record<string, string> = {
  STABLECOIN: 'Stablecoin',
  RECEIVABLE: 'Recebíveis',
  REAL_ESTATE: 'Imóveis',
  FUND_SHARE: 'Cotas de Fundos',
  FIXED_INCOME: 'Renda Fixa',
  COMMODITY: 'Commodities',
  PRECATORY: 'Precatórios'
};

export function AssetOrigination() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState<AssetFormData>({
    name: '',
    symbol: '',
    category: 'RECEIVABLE',
    description: '',
    totalValue: 0,
    unitPrice: 100 * 100,
    yieldFrequency: 'MONTHLY'
  });

  // Smart Contract Template Selection
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedNetwork, setSelectedNetwork] = useState<string>('POLYGON_MUMBAI');
  const [showCodePreview, setShowCodePreview] = useState(false);
  const [showDeployPreview, setShowDeployPreview] = useState(false);

  // Fetch templates filtered by token standard (ERC20 for most assets)
  const { data: templatesData } = useTemplates({ tokenStandard: 'ERC20', isActive: true });
  const templates = templatesData?.data || [];
  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  // Generate deploy preview
  const generatePreviewMutation = useGeneratePreview();
  const deployPreview = generatePreviewMutation.data;

  const { data: asset, isLoading } = useQuery<Asset>({
    queryKey: ['asset', id],
    queryFn: async () => {
      const res = await api.get(`/tokenization/assets/${id}`);
      return res.data;
    },
    enabled: isEditing
  });

  // Update form when asset data loads
  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name,
        symbol: asset.symbol,
        category: asset.category,
        description: asset.description || '',
        totalValue: asset.totalValue,
        unitPrice: asset.unitPrice,
        maturityDate: asset.maturityDate?.split('T')[0],
        yieldRate: asset.yieldRate,
        yieldFrequency: asset.yieldFrequency
      });
    }
  }, [asset]);

  const { data: enabledCategories } = useQuery({
    queryKey: ['enabled-categories'],
    queryFn: async () => {
      const res = await api.get('/tokenization/tenant-categories/enabled');
      // API may return objects with category field or plain strings
      const data = res.data;
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        return data.map((item: any) => item.category || item.categoryId || item.categoryName);
      }
      return data as string[];
    }
  });

  const saveMutation = useMutation({
    mutationFn: async (data: AssetFormData) => {
      if (isEditing) {
        return api.put(`/tokenization/assets/${id}`, data);
      }
      return api.post('/tokenization/assets', data);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      if (!isEditing) {
        navigate(`/admin/tokenization/assets/${res.data.id}`);
      }
    }
  });

  const submitMutation = useMutation({
    mutationFn: () => api.post(`/tokenization/assets/${id}/submit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      navigate('/admin/tokenization/assets');
    }
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'ASSET_DOCUMENT');
      return api.post(`/tokenization/assets/${id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['asset', id] })
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      newFiles.forEach(file => uploadMutation.mutate(file));
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-surface-800 rounded w-64" />
          <div className="h-96 bg-surface-800 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-surface-800 rounded-lg">
          <ArrowLeft className="w-5 h-5 text-surface-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">
            {isEditing ? 'Editar Ativo' : 'Novo Ativo Tokenizável'}
          </h1>
          <p className="text-surface-400">
            {isEditing ? `Editando ${asset?.name}` : 'Preencha os dados do ativo para tokenização'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Informações Básicas</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm text-surface-400 mb-1">Nome do Ativo</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  placeholder="Ex: Token de Recebíveis XYZ"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Símbolo</label>
                <input
                  type="text"
                  value={formData.symbol}
                  onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  placeholder="Ex: TRCXYZ"
                  maxLength={10}
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Categoria</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  required
                >
                  {(enabledCategories || Object.keys(categoryLabels)).map(cat => (
                    <option key={cat} value={cat}>{categoryLabels[cat] || cat}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <label className="block text-sm text-surface-400 mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  rows={4}
                  placeholder="Descreva o ativo, sua origem e características..."
                />
              </div>
            </div>
          </Card>

          {/* Financial Info */}
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Informações Financeiras</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-surface-400 mb-1">Valor Total (R$)</label>
                <input
                  type="number"
                  value={formData.totalValue / 100}
                  onChange={(e) => setFormData({ ...formData, totalValue: Number(e.target.value) * 100 })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  min={0}
                  step={0.01}
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Preço Unitário (R$)</label>
                <input
                  type="number"
                  value={formData.unitPrice / 100}
                  onChange={(e) => setFormData({ ...formData, unitPrice: Number(e.target.value) * 100 })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  min={0.01}
                  step={0.01}
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Data de Vencimento</label>
                <input
                  type="date"
                  value={formData.maturityDate || ''}
                  onChange={(e) => setFormData({ ...formData, maturityDate: e.target.value })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                />
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Taxa de Rendimento (% a.a.)</label>
                <input
                  type="number"
                  value={formData.yieldRate || ''}
                  onChange={(e) => setFormData({ ...formData, yieldRate: Number(e.target.value) })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                  min={0}
                  step={0.01}
                />
              </div>

              <div>
                <label className="block text-sm text-surface-400 mb-1">Frequência de Pagamento</label>
                <select
                  value={formData.yieldFrequency || ''}
                  onChange={(e) => setFormData({ ...formData, yieldFrequency: e.target.value })}
                  className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                >
                  <option value="">Sem rendimento</option>
                  <option value="MONTHLY">Mensal</option>
                  <option value="QUARTERLY">Trimestral</option>
                  <option value="SEMI_ANNUAL">Semestral</option>
                  <option value="ANNUAL">Anual</option>
                  <option value="AT_MATURITY">No Vencimento</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Documents */}
          {isEditing && (
            <Card className="p-6">
              <h3 className="font-semibold text-surface-100 mb-4">Documentos</h3>
              
              <div className="border-2 border-dashed border-surface-700 rounded-lg p-6 text-center mb-4">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  multiple
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-surface-500 mx-auto mb-2" />
                  <p className="text-surface-400">Clique para fazer upload ou arraste arquivos</p>
                  <p className="text-xs text-surface-500 mt-1">PDF, DOC, DOCX, JPG, PNG</p>
                </label>
              </div>

              {(asset?.documents?.length ?? 0) > 0 && (
                <div className="space-y-2">
                  {asset?.documents?.map((doc: any) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-surface-400" />
                        <div>
                          <p className="text-surface-100">{doc.name}</p>
                          <p className="text-xs text-surface-500">{doc.type}</p>
                        </div>
                      </div>
                      <Badge variant={doc.status === 'APPROVED' ? 'success' : 'warning'}>
                        {doc.status === 'APPROVED' ? 'Aprovado' : 'Pendente'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* Smart Contract Template Selection */}
          {isEditing && asset?.status === 'APPROVED' && (
            <Card className="p-6">
              <h3 className="font-semibold text-surface-100 mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-primary-400" />
                Smart Contract
              </h3>
              
              <div className="space-y-4">
                {/* Template Selection */}
                <div>
                  <label className="block text-sm text-surface-400 mb-2">Template de Contrato</label>
                  <TemplateSelector
                    tokenStandard="ERC20"
                    onSelect={(template) => setSelectedTemplateId(template.id)}
                    showAuditStatus
                  />
                </div>

                {/* Selected Template Info */}
                {selectedTemplate && (
                  <div className="p-4 rounded-lg bg-surface-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-surface-100">{selectedTemplate.name}</span>
                      <AuditStatusBadge status={selectedTemplate.auditStatus} showDetails />
                    </div>
                    <p className="text-sm text-surface-400">{selectedTemplate.description}</p>
                    <div className="flex items-center gap-2 text-xs text-surface-500">
                      <span>Versão: {selectedTemplate.currentVersion}</span>
                      <span>•</span>
                      <span>Padrão: {selectedTemplate.tokenStandard}</span>
                    </div>

                    {/* Audit Warning */}
                    {selectedTemplate.auditStatus !== 'AUDITED' && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                        <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-yellow-200">
                          Este template não foi auditado. Deploy em mainnet não será permitido.
                        </span>
                      </div>
                    )}

                    {/* Code Preview Button */}
                    <button
                      type="button"
                      onClick={() => setShowCodePreview(!showCodePreview)}
                      className="flex items-center gap-2 text-sm text-primary-400 hover:text-primary-300"
                    >
                      <Eye className="w-4 h-4" />
                      {showCodePreview ? 'Ocultar código' : 'Ver código do contrato'}
                    </button>

                    {showCodePreview && (
                      <div className="mt-3">
                        <SolidityCodeViewer 
                          code={selectedTemplate.sourceCode} 
                          height="300px" 
                          readOnly 
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Network Selection */}
                {selectedTemplate && (
                  <div>
                    <label className="block text-sm text-surface-400 mb-2">Rede de Deploy</label>
                    <select
                      value={selectedNetwork}
                      onChange={(e) => setSelectedNetwork(e.target.value)}
                      className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-2 text-surface-100"
                    >
                      <optgroup label="Testnets (Recomendado para testes)">
                        <option value="POLYGON_MUMBAI">Polygon Mumbai (Testnet)</option>
                        <option value="ETHEREUM_SEPOLIA">Ethereum Sepolia (Testnet)</option>
                        <option value="BSC_TESTNET">BSC Testnet</option>
                      </optgroup>
                      {selectedTemplate.auditStatus === 'AUDITED' && (
                        <optgroup label="Mainnets (Produção)">
                          <option value="POLYGON">Polygon</option>
                          <option value="ETHEREUM">Ethereum</option>
                          <option value="BSC">BSC</option>
                          <option value="ARBITRUM">Arbitrum</option>
                          <option value="BASE">Base</option>
                        </optgroup>
                      )}
                    </select>
                  </div>
                )}

                {/* Generate Preview Button */}
                {selectedTemplate && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      generatePreviewMutation.mutate({
                        templateId: selectedTemplate.id,
                        dto: {
                          network: selectedNetwork as any,
                          params: {
                            name: formData.name,
                            symbol: formData.symbol,
                            decimals: 18,
                            initialSupply: String(Math.floor(formData.totalValue / formData.unitPrice)),
                          }
                        }
                      });
                      setShowDeployPreview(true);
                    }}
                    disabled={generatePreviewMutation.isPending}
                  >
                    {generatePreviewMutation.isPending ? 'Gerando preview...' : 'Gerar Preview de Deploy'}
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Deploy Preview Modal */}
          {showDeployPreview && deployPreview && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="max-w-4xl w-full max-h-[90vh] overflow-auto">
                <DeployPreviewPanel
                  preview={deployPreview}
                  isLoading={false}
                  onConfirm={() => {
                    // TODO: Execute actual deploy
                    alert('Deploy será implementado na próxima fase');
                    setShowDeployPreview(false);
                  }}
                  onCancel={() => setShowDeployPreview(false)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Resumo</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-surface-400">Tokens a emitir</span>
                <span className="text-surface-100 font-medium">
                  {formData.unitPrice > 0 ? Math.floor(formData.totalValue / formData.unitPrice).toLocaleString('pt-BR') : 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Valor Total</span>
                <span className="text-surface-100 font-medium">
                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(formData.totalValue / 100)}
                </span>
              </div>
              {formData.yieldRate && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Rendimento</span>
                  <span className="text-green-400 font-medium">{formData.yieldRate}% a.a.</span>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-surface-100 mb-4">Ações</h3>
            <div className="space-y-3">
              <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
                <Save className="w-4 h-4 mr-2" />
                {saveMutation.isPending ? 'Salvando...' : 'Salvar Rascunho'}
              </Button>
              
              {isEditing && asset?.status === 'DRAFT' && (
                <Button
                  type="button"
                  variant="primary"
                  className="w-full"
                  onClick={() => submitMutation.mutate()}
                  disabled={submitMutation.isPending}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Submeter para Aprovação
                </Button>
              )}
            </div>
          </Card>

          {isEditing && (
            <Card className="p-6">
              <h3 className="font-semibold text-surface-100 mb-2">Status</h3>
              <Badge variant={
                asset?.status === 'ACTIVE' ? 'success' :
                asset?.status === 'PENDING_APPROVAL' ? 'warning' :
                asset?.status === 'DRAFT' ? 'info' : 'error'
              } className="text-sm">
                {asset?.status === 'ACTIVE' ? 'Ativo' :
                 asset?.status === 'PENDING_APPROVAL' ? 'Aguardando Aprovação' :
                 asset?.status === 'DRAFT' ? 'Rascunho' :
                 asset?.status === 'REJECTED' ? 'Rejeitado' : asset?.status}
              </Badge>
            </Card>
          )}
        </div>
      </form>
    </div>
  );
}

export default AssetOrigination;
